import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LinkIcon, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAccountGroupPartnerFunction = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_account_group_partner_function');
  const [viewItem, setViewItem] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [partnerFunctions, setPartnerFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [formData, setFormData] = useState({
    account_group_id: '',
    partner_function_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, groupsRes, functionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer-account-group-partner-functions`),
        axios.get(`${BACKEND_URL}/api/customer-account-groups`),
        axios.get(`${BACKEND_URL}/api/customer-partner-functions`)
      ]);
      
      setAssignments(assignmentsRes.data);
      setAccountGroups(groupsRes.data);
      setPartnerFunctions(functionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.account_group_id || !formData.partner_function_id) {
      toast.error('Please select both Account Group and Partner Function');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-account-group-partner-functions/${currentAssignment.assignment_id}`,
          { partner_function_id: formData.partner_function_id }
        );
        toast.success('Assignment updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-account-group-partner-functions`, formData);
        toast.success('Assignment created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleEdit = (assignment) => {
    setCurrentAssignment(assignment);
    setFormData({
      account_group_id: assignment.account_group_id,
      partner_function_id: assignment.partner_function_id
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ account_group_id: '', partner_function_id: '' });
    setCurrentAssignment(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  const getAccountGroupDescription = (id) => {
    const group = accountGroups.find(g => g.account_group_id === id);
    return group?.description || id;
  };

  const getPartnerFunctionName = (id) => {
    const pf = partnerFunctions.find(p => p.partner_function_id === id);
    return pf?.name || id;
  };

  const getPartnerFunctionType = (id) => {
    const pf = partnerFunctions.find(p => p.partner_function_id === id);
    return pf?.partner_type || '';
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Partner Function Assignments</h1>
                <p className="text-sm text-gray-600">Assign partner functions to customer account groups</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
              <p className="text-sm text-gray-600">Loading assignments...</p>
            </div>
          ) : assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <LinkIcon className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No assignments yet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Assign partner functions to account groups to define customer relationships
              </p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Assignment
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Group</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Function</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Function Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {assignments.map((assignment) => (
                    <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{assignment.account_group_id}</span>
                      </td>
                      <td className="px-6 py-4">{getAccountGroupDescription(assignment.account_group_id)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-orange-900">{assignment.partner_function_id}</span>
                      </td>
                      <td className="px-6 py-4">{getPartnerFunctionName(assignment.partner_function_id)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          getPartnerFunctionType(assignment.partner_function_id) === 'Customer' 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {getPartnerFunctionType(assignment.partner_function_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_account_group_partner_function" onView={() => setViewItem(assignment)} onEdit={() => handleEdit(assignment)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Assignment' : 'Add Assignment'}
                </h2>
                <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Customer Account Group *</Label>
                  <select
                    value={formData.account_group_id}
                    onChange={(e) => setFormData({ ...formData, account_group_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={editMode}
                  >
                    <option value="">Select Account Group</option>
                    {editMode && currentAssignment ? (
                      <option value={currentAssignment.account_group_id}>
                        {currentAssignment.account_group_id} - {getAccountGroupDescription(currentAssignment.account_group_id)}
                      </option>
                    ) : (
                      accountGroups.map(group => (
                        <option key={group.account_group_id} value={group.account_group_id}>
                          {group.account_group_id} - {group.description}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <Label>Partner Function *</Label>
                  <select
                    value={formData.partner_function_id}
                    onChange={(e) => setFormData({ ...formData, partner_function_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Partner Function</option>
                    {partnerFunctions.map(pf => (
                      <option key={pf.partner_function_id} value={pf.partner_function_id}>
                        {pf.partner_function_id} - {pf.name} ({pf.partner_type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-700">
                    <strong>Note:</strong> {editMode 
                      ? 'You can change the partner function for this account group.'
                      : 'Each account group can have multiple partner functions, but each partner function can only be assigned once to an account group.'}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerAccountGroupPartnerFunction;
